import { useContext, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Badge } from 'reactstrap';
// @ts-ignore
import nestedProperty from 'nested-property';
import {
  allStoreTiersQuery,
  integrationOptionsQuery,
  creditToPartnerOptionsQuery,
  productCategories as productCategoriesQuery,
} from '../../graphql/queries';
import { updateStoreSuperAdminMutation } from '../../graphql/mutations';
import { DateTimeHelpers } from '../../tools/DateTimeHelpers';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { getObjectDiff } from '@/utils/objDiff';
import {
  EditItem,
  EditSection,
  LoadingContainer,
} from '../../components/Layout';
import {
  CheckBoxHelpText,
  CheckboxLabel,
  VytalCheckboxLabelContainer,
} from '../../pages/AdminStoreDetail/Menu/ProductItem/ProductOrderSheet/ExtraSelector/ExtraGroupEditModal/styled';
import Checkbox from '../../components/Checkbox';
import { Communicator } from '../../tools/Communication';
import { LayoutStoreInstance } from '../../LayoutStore';
import StoreTypeSelector from './StoreTypeSelector';
import { FaTrash } from 'react-icons/all';
import moment from 'moment';
import { useHistory } from 'react-router';
import { MerchantStore, UserInfo } from './MerchantModel';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { useStoreQuery } from '../../graphql/queries/storeAdminInfo.generated';
import { StoreAdminFragmentFragment } from '../../graphql/fragments/storeAdminInfo.generated';
import { OnboardingStatus, StoreType } from '../../graphql/generatedTypes';
import { hasMeaningfulContractEndDate } from '@/utils/contractEndDate';
import { getCurrentDatePlusDays } from '@/utils/date';
import { ContentSection } from '../../components/Layout/ContentSection';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';

type MerchantContractDataTabProps = {
  externalPickpackId: string | null;
  title: string;
};

export const MerchantGeneralAdminInfoTab = ({
  externalPickpackId,
  title,
}: MerchantContractDataTabProps) => {
  const viewer = useContext(ViewerContext);
  const history = useHistory();

  const [storeUpdate, setStoreUpdate] = useState<StoreAdminFragmentFragment>(
    null as any,
  );
  const [oldStoreUpdate, setOldStoreUpdate] =
    useState<StoreAdminFragmentFragment>(null as any);
  const [updateStoreSuperAdmin, { loading: updatingSuperAdminInfo }] =
    useMutation(updateStoreSuperAdminMutation);
  const [regions, setRegions] = useState(null as any[] | null);
  const [containerInformation, setContainerInformation] = useState(
    null as any[] | null,
  );
  const [vytalStore, setVytalStore] = useState({} as MerchantStore);
  const [enableInventoryChecks, setEnableInventoryChecks] = useState(false);

  const inventoryChecksFromStore =
    storeUpdate?.allow_inventory_checks_for_terminal_users_until !== null &&
    storeUpdate?.allow_inventory_checks_for_terminal_users_until !== undefined;

  useEffect(() => {
    setEnableInventoryChecks(inventoryChecksFromStore);
  }, [inventoryChecksFromStore]);

  const allowedToAccess = CanAccessPartnerData(viewer);

  function resetStore(storeIn: any) {
    setStoreUpdate(storeIn);
    setOldStoreUpdate(JSON.parse(JSON.stringify(storeIn)));
  }

  const { data = { store: null }, loading: loadingStore } = useStoreQuery({
    fetchPolicy: 'no-cache',
    skip: !externalPickpackId,
    variables: { id: externalPickpackId || '' },
    onCompleted: (storeData) => {
      // @ts-ignore
      if (storeData) {
        resetStore(storeData.store);
      }
    },
  });

  const { data: storeTiersData = { __type: { enumValues: [] } } } =
    useQuery(allStoreTiersQuery);
  const storeTiers: any = ['-'].concat(
    storeTiersData.__type.enumValues.map((storeTier: any) => storeTier.name),
  );

  const { data: integrationOptionsData } = useQuery(integrationOptionsQuery);
  const integrationOptions = (
    integrationOptionsData?.integrationOptions || []
  ).map((opt: any) => ({
    label: opt.name,
    value: opt.api_name,
  }));

  const { data: creditToPartnerOptionsData } = useQuery(
    creditToPartnerOptionsQuery,
  );
  const creditToPartnerOptions = (
    creditToPartnerOptionsData?.creditToPartnerOptions || []
  ).map((opt: any) => ({
    label: opt.name,
    value: opt.api_name,
  }));

  // Load region list if it was not loaded yet
  useEffect(() => {
    Communicator.Get('/Merchant/GetRegions', {})
      .then(async (response) => {
        const regionData = (await response.json()) as any[];
        setRegions(regionData);
      })
      .catch((error) => {
        LayoutStoreInstance.setError('Failed to load region list: ' + error);
      });
    Communicator.Get('/Merchant/GetMerchantStoreByPickpackId', {
      storeId: externalPickpackId,
    })
      .then(async (response) => {
        const storeData = (await response.json()) as MerchantStore;
        setContainerInformation(storeData.containerInformation);
        setVytalStore(storeData);
      })
      .catch((error) => {
        LayoutStoreInstance.setError(
          'Failed to load container information: ' + error,
        );

        const appUsers: UserInfo[] = [];
        setVytalStore({ appUsers } as MerchantStore);
      });
  }, []);

  if (loadingStore || !storeUpdate) {
    return <LoadingContainer />;
  }

  const store: any = data.store;

  // @ts-ignore
  function handleChange({ target }) {
    const { name, value } = target;
    const isNumber = target.hasAttribute('data-isnumber');
    const isDate = target.hasAttribute('data-isdate');
    let formattedValue = value;

    if (isNumber) formattedValue = Number(value);

    if (isDate) {
      const date = (target as EventTarget & HTMLInputElement).valueAsDate;
      if (date != null) {
        formattedValue = DateTimeHelpers.toDateTimeOffsetString(date);
      } else {
        formattedValue = DateTimeHelpers.toDateTimeOffsetString(new Date(0));
      }
    }

    setStoreUpdate((oldStoreUpdate: any) => {
      let newStoreUpdate = { ...oldStoreUpdate };
      nestedProperty.set(newStoreUpdate, name, formattedValue);
      return newStoreUpdate;
    });
  }

  function handleSaveSuperAdmin() {
    if (hasInvalidAdditionalAllowance()) {
      if (
        confirm(
          'Are you sure you want to save the additional allowance without a valid until date?',
        ) === false
      ) {
        return;
      }
    }

    const getMutationInput = (storeIn: any) => ({
      type: storeIn.type,
      invoice_headline: storeIn.invoice_headline,
      onboarding_fee_vytal: isNaN(parseInt(storeIn.onboarding_fee_vytal))
        ? null
        : parseInt(storeIn.onboarding_fee_vytal),
      invoice_email: (storeIn.invoice_email || '').trim() || null,
      legal_name: (storeIn.legal_name || '').trim() || null,
      invoice_address: {
        ...(storeIn.invoice_address || {}),
        __typename: undefined,
      },
      custom_invoice_items: storeIn.custom_invoice_items
        ? storeIn.custom_invoice_items.map((item: object) => ({
            ...item,
            __typename: undefined,
          }))
        : null,
      contracted_date: storeIn.contracted_date,
      onboarded_date: storeIn.onboarded_date,
      test_period_end: storeIn.test_period_end,
      coming_soon: storeIn.coming_soon,
      temporarily_unavailable: storeIn.temporarily_unavailable,
      published: storeIn.published,
      can_preorder: storeIn.can_preorder,
      can_deliver: storeIn.can_deliver,
      feature_container_collection_enabled:
        storeIn.feature_container_collection_enabled,
      always_online: storeIn.always_online,
      show_in_stats: storeIn.show_in_stats,
      region: parseInt(storeIn.region),
      available_soon: storeIn.available_soon,
      available_from: storeIn.available_from,
      has_wolt: storeIn.has_wolt,
      tier: storeIn.tier === '-' ? null : storeIn.tier,
      revenue_tier: storeIn.revenue_tier === '-' ? null : storeIn.revenue_tier,
      allow_inventory_checks_for_terminal_users:
        storeIn.allow_inventory_checks_for_terminal_users_permanently,
      allow_inventory_checks_for_terminal_users_until:
        storeIn.allow_inventory_checks_for_terminal_users_until,
      non_usage_fee_enabled: storeIn.non_usage_fee_enabled,
      dpd_pickup_compulsory: storeIn.dpd_pickup_compulsory,
      additional_allowance: parseInt(storeIn.additional_allowance, 10),
      additional_allowance_valid_until:
        storeIn.additional_allowance_valid_until,
      permanently_closed: storeIn.permanently_closed,
      employee_only: storeIn.employee_only,
      send_nuf_info_to_invoice_address:
        storeIn.send_nuf_info_to_invoice_address,
      nuf_comment: storeIn.nuf_comment,
      excluded_from_nuf_until: storeIn.excluded_from_nuf_until,
      can_order_secondary_portfolio: storeIn.can_order_secondary_portfolio,
      default_inventory_check_expiration_days: Number(
        storeIn.default_inventory_check_expiration_days,
      ),
      new_inventory_checks_are_non_billable:
        storeIn.new_inventory_checks_are_non_billable,
      order_preferences: {
        ...storeIn.order_preferences,
        __typename: undefined,
      },
      onboarding_status: storeIn.onboarding_status || null,
      integrations: storeIn.integrations || [],
      credit_to_partner: storeIn.credit_to_partner || null,
    });

    const mutationInput = getObjectDiff(
      getMutationInput(oldStoreUpdate),
      getMutationInput(storeUpdate),
    );

    console.log('mutationInput admin', mutationInput);

    updateStoreSuperAdmin({
      variables: {
        id: store.id,
        store: mutationInput,
      },
      refetchQueries: [
        {
          query: productCategoriesQuery,
          variables: {
            id: store.id,
            filter: 'ALL',
          },
        },
      ],
      update: (cache, { data: { updateStoreSuperAdmin: updatedStore } }) => {
        resetStore(updatedStore);
      },
    }).catch((err: any) => {
      alert('Error updating store as super admin: ' + err);
    });
  }

  function handleChangeManually(newVal: any, name: string) {
    console.log('handleChangeManually', newVal, name);
    setStoreUpdate({
      ...(storeUpdate as StoreAdminFragmentFragment),
      [name]: newVal,
    });
  }

  function toggleAllowMepal() {
    const newVal = !storeUpdate.order_preferences?.allow_mepal;
    setStoreUpdate({
      ...storeUpdate,
      order_preferences: {
        ...(storeUpdate.order_preferences || {}),
        allow_mepal: newVal,
        allow_koziol: !newVal,
      },
    });
  }

  function toggleAllowKoziol() {
    const newVal = !storeUpdate.order_preferences?.allow_koziol;
    setStoreUpdate({
      ...storeUpdate,
      order_preferences: {
        ...(storeUpdate.order_preferences || {}),
        allow_koziol: newVal,
        allow_mepal: !newVal,
      },
    });
  }

  function hasInvalidAdditionalAllowance() {
    const additionalAllowance = parseInt(
      String(storeUpdate.additional_allowance),
      10,
    );

    if (isNaN(additionalAllowance) || additionalAllowance <= 0) {
      return false;
    }

    if (
      storeUpdate.additional_allowance_valid_until == null ||
      storeUpdate.additional_allowance_valid_until <=
        getCurrentDatePlusDays(0).toISOString()
    ) {
      return true;
    }
    return false;
  }

  function handlePublishedChanged() {
    if (
      !storeUpdate.published &&
      containerInformation &&
      (!containerInformation.every((i) => i.isUnwanted) ||
        window.confirm(
          'Are you sure you want to publish this store with all container types marked as unwanted?',
        ))
    ) {
      handleChangeManually(!storeUpdate.published, 'published');
    } else if (storeUpdate.published || !containerInformation) {
      handleChangeManually(!storeUpdate.published, 'published');
    }
  }

  function handleCanScanContainerCollectionsChange() {
    setVytalStore({
      ...vytalStore,
      canScanContainerCollections: !vytalStore.canScanContainerCollections,
    });
  }

  if (!externalPickpackId) return <p>externalPickpackId is not avaliable</p>;

  if (!store) return <p>Store {externalPickpackId} could not be loaded</p>;

  if (!allowedToAccess) {
    return null;
  }

  const isInventoryCheckActivePermanent =
    storeUpdate.allow_inventory_checks_for_terminal_users_permanently;
  const isInventoryCheckAllowed =
    (storeUpdate.allow_inventory_checks_for_terminal_users_until &&
      new Date(storeUpdate.allow_inventory_checks_for_terminal_users_until) >=
        new Date()) ||
    false;

  const appVersions = vytalStore?.appUsers
    ?.map((u) => {
      return u?.userInformation?.clientVersion;
    })
    .filter((cV) => {
      return cV !== null;
    });

  const isOutdatedVersion = (version: string | undefined | null) => {
    if (!version) {
      return false;
    }

    const [mayor, minor] = version.split('.');
    if (Number(minor) < 10 && Number(mayor) <= 6) {
      return true;
    }

    return false;
  };

  const hasOneUpToDateApp = appVersions?.some((version) => {
    return !isOutdatedVersion(version);
  });

  // Offboarding and Terminated need a real contract end date. A legacy row
  // already saved with one of them keeps it selectable, so the operator can
  // still see and change the current value.
  const hasContractEndDate = hasMeaningfulContractEndDate(
    storeUpdate?.contract_end_date,
  );
  const contractEndingOption = (status: OnboardingStatus, label: string) => (
    <option
      value={status}
      disabled={
        !hasContractEndDate && oldStoreUpdate?.onboarding_status !== status
      }
    >
      {hasContractEndDate ? label : `${label} (no date set)`}
    </option>
  );

  return (
    <ContentSection title={title} collapsable={false} key="content">
      <EditSection
        title={i18n.t('Merchants:generalAdminInfo')}
        isLoading={updatingSuperAdminInfo}
        onSave={handleSaveSuperAdmin}
      >
        <EditItem title={i18n.t('Merchants:storeRegion')}>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Merchants:storeRegion')}
            </span>
            <select
              className="form-control custom-select"
              name="region"
              onChange={handleChange}
              value={storeUpdate.region || 0}
            >
              {!regions
                ? null
                : regions.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
            </select>
          </div>
        </EditItem>
        <EditItem title={i18n.t('Merchants:storeType')}>
          <StoreTypeSelector
            storeType={storeUpdate.type || 'RESTAURANT'}
            onChange={(updatedStoreType: StoreType) =>
              setStoreUpdate({
                ...storeUpdate,
                type: updatedStoreType,
              })
            }
          />
        </EditItem>
        <EditItem title={i18n.t('Merchants:storeTier')}>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Merchants:startTier')}
            </span>
            <select
              className="form-control custom-select"
              name="tier"
              value={storeUpdate.tier || '-'}
              onChange={handleChange}
            >
              {storeTiers.map((t: string) => (
                <option>{t}</option>
              ))}
            </select>
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Merchants:revenueTier')}
            </span>
            <select
              className="form-control custom-select"
              name="revenue_tier"
              value={storeUpdate.revenue_tier || '-'}
              onChange={handleChange}
              disabled
            >
              {storeTiers.map((t: string) => (
                <option>{t}</option>
              ))}
            </select>
          </div>
        </EditItem>
        <EditItem title="Status">
          <div className="input-group mb-3">
            <span className="input-group-text">Status</span>
            <select
              className="form-control custom-select"
              name="onboarding_status"
              value={storeUpdate.onboarding_status || ''}
              onChange={handleChange}
            >
              <option value="">-</option>
              <option value={OnboardingStatus.Open}>Open</option>
              <option value={OnboardingStatus.WonOnboarding}>
                Won/Onboarding
              </option>
              <option value={OnboardingStatus.Active}>Active</option>
              {/*
                The contract end date lives on the Contract Data tab, and objDiff
                only sends changed fields, so without this the status could be
                saved alone and leave the store with no date. The backend
                enforces the same rule on save.
              */}
              {contractEndingOption(
                OnboardingStatus.Offboarding,
                'Offboarding',
              )}
              {contractEndingOption(OnboardingStatus.Terminated, 'Terminated')}
            </select>
          </div>
        </EditItem>
        <EditItem title="Integrations">
          <div className="input-group mb-3">
            <span className="input-group-text">Integrations</span>
            <MultiSelect
              style={{ flex: 'auto' }}
              value={storeUpdate.integrations || []}
              options={integrationOptions}
              onChange={(e) => handleChangeManually(e.value, 'integrations')}
              placeholder="Select integrations"
              maxSelectedLabels={5}
            />
          </div>
        </EditItem>
        <EditItem title="Credit to Partner">
          <div className="input-group mb-3">
            <span className="input-group-text">Credit to Partner</span>
            <Dropdown
              style={{ flex: 'auto' }}
              value={storeUpdate.credit_to_partner || null}
              options={creditToPartnerOptions}
              onChange={(e) =>
                handleChangeManually(e.value, 'credit_to_partner')
              }
              placeholder="Select credit to partner"
              showClear
            />
          </div>
        </EditItem>
        <EditItem title={i18n.t('Merchants:inventoryChecksForPartner')}>
          {storeUpdate.allow_inventory_checks_for_terminal_users && (
            <p>The partner can currently do inventory checks by themselves.</p>
          )}

          {!storeUpdate.allow_inventory_checks_for_terminal_users && (
            <p>
              The partner can currently <b>NOT</b> do inventory checks by
              themselves.
            </p>
          )}

          <div className="input-group mb-3">
            <div className="input-group mb-3">
              {i18n.t('InventoryCheck:partnerAppVersions')}:{' '}
              {hasOneUpToDateApp === undefined && (
                <div className="ms-2">
                  <LoadingContainer />
                </div>
              )}
              {appVersions?.map((version) => {
                if (!version) {
                  return null;
                }

                const isOutdated = isOutdatedVersion(version);

                const [mayor, minor, patch] = version?.split('.');

                return (
                  <div className="ms-1">
                    <Badge
                      color={isOutdated ? 'warning' : 'success'}
                      title={version}
                      pill
                    >
                      {`${mayor}.${minor}.${patch}`}
                    </Badge>
                  </div>
                );
              })}
            </div>

            <div className="input-group mb-3">
              <Badge color={hasOneUpToDateApp ? 'success' : 'warning'} pill>
                {hasOneUpToDateApp !== undefined &&
                  (hasOneUpToDateApp
                    ? i18n.t('InventoryCheck:oneOrMoreDevicesReady')
                    : i18n.t('InventoryCheck:outdatedDevices'))}
              </Badge>
            </div>

            <div className="input-group mb-3">
              <VytalCheckboxLabelContainer>
                <Checkbox
                  disabled={
                    isInventoryCheckActivePermanent ||
                    hasOneUpToDateApp === undefined ||
                    false
                  }
                  checked={enableInventoryChecks}
                  onClick={() => {
                    if (!enableInventoryChecks) {
                      setEnableInventoryChecks(!enableInventoryChecks);

                      if (
                        storeUpdate?.allow_inventory_checks_for_terminal_users_until ===
                          null ||
                        storeUpdate?.allow_inventory_checks_for_terminal_users_until ===
                          undefined
                      ) {
                        const date = new Date();
                        date.setDate(date.getDate() + 28);

                        handleChangeManually(
                          DateTimeHelpers.toDateInputFromUtcString(
                            date.toISOString(),
                          ),
                          'allow_inventory_checks_for_terminal_users_until',
                        );
                      }
                    } else {
                      setEnableInventoryChecks(!enableInventoryChecks);

                      handleChangeManually(
                        null,
                        'allow_inventory_checks_for_terminal_users_until',
                      );
                    }
                  }}
                />

                <CheckboxLabel
                  onClick={() => {
                    const isDisabled =
                      isInventoryCheckActivePermanent ||
                      hasOneUpToDateApp === undefined ||
                      false;

                    if (!isDisabled) {
                      if (!enableInventoryChecks) {
                        setEnableInventoryChecks(!enableInventoryChecks);

                        if (
                          storeUpdate?.allow_inventory_checks_for_terminal_users_until ===
                            null ||
                          storeUpdate?.allow_inventory_checks_for_terminal_users_until ===
                            undefined
                        ) {
                          const date = new Date();
                          date.setDate(date.getDate() + 28);

                          handleChangeManually(
                            DateTimeHelpers.toDateInputFromUtcString(
                              date.toISOString(),
                            ),
                            'allow_inventory_checks_for_terminal_users_until',
                          );
                        }
                      } else {
                        setEnableInventoryChecks(!enableInventoryChecks);

                        handleChangeManually(
                          null,
                          'allow_inventory_checks_for_terminal_users_until',
                        );
                      }
                    }
                  }}
                >
                  {i18n.t('InventoryCheck:enable')}
                </CheckboxLabel>
              </VytalCheckboxLabelContainer>
            </div>
            <div className="input-group mb-3">
              <VytalCheckboxLabelContainer>
                <Checkbox
                  checked={
                    storeUpdate.allow_inventory_checks_for_terminal_users_permanently ||
                    false
                  }
                  disabled={hasOneUpToDateApp === undefined}
                  onClick={() =>
                    handleChangeManually(
                      !storeUpdate.allow_inventory_checks_for_terminal_users_permanently,
                      'allow_inventory_checks_for_terminal_users_permanently',
                    )
                  }
                />
                <CheckboxLabel
                  onClick={() => {
                    const isDisabled = hasOneUpToDateApp === undefined;

                    if (!isDisabled) {
                      handleChangeManually(
                        !storeUpdate.allow_inventory_checks_for_terminal_users_permanently,
                        'allow_inventory_checks_for_terminal_users_permanently',
                      );
                    }
                  }}
                >
                  {i18n.t('Merchants:allowInventoryChecksPermanently')}
                  <CheckBoxHelpText>
                    {i18n.t('Merchants:allowInventoryChecksPermanentlyHelp')}
                  </CheckBoxHelpText>
                </CheckboxLabel>
              </VytalCheckboxLabelContainer>
            </div>

            {enableInventoryChecks && !isInventoryCheckActivePermanent && (
              <div className="input-group mb-3">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('Merchants:allowInventoryChecksUntil')}
                </span>

                <input
                  type="date"
                  className="form-control"
                  onBlur={(e) => {
                    handleChangeManually(
                      e.target.value,
                      'allow_inventory_checks_for_terminal_users_until',
                    );
                  }}
                  defaultValue={DateTimeHelpers.toDateInputFromUtcString(
                    storeUpdate.allow_inventory_checks_for_terminal_users_until,
                  )}
                />
              </div>
            )}

            {(isInventoryCheckActivePermanent || isInventoryCheckAllowed) && (
              <div className="input-group mb-3">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('Merchants:defaultInventoryCheckExpirationDays')}
                </span>
                <input
                  type="string"
                  className="form-control"
                  onChange={(e) =>
                    handleChangeManually(
                      e.target.value.replace(/[^0-9]/g, ''),
                      'default_inventory_check_expiration_days',
                    )
                  }
                  value={storeUpdate.default_inventory_check_expiration_days}
                />
              </div>
            )}

            <div className="input-group mb-3">
              <VytalCheckboxLabelContainer>
                <Checkbox
                  checked={
                    storeUpdate.new_inventory_checks_are_non_billable || false
                  }
                  onClick={() =>
                    handleChangeManually(
                      !storeUpdate.new_inventory_checks_are_non_billable,
                      'new_inventory_checks_are_non_billable',
                    )
                  }
                />
                <CheckboxLabel
                  onClick={() =>
                    handleChangeManually(
                      !storeUpdate.new_inventory_checks_are_non_billable,
                      'new_inventory_checks_are_non_billable',
                    )
                  }
                >
                  {i18n.t('Merchants:newInventoryChecksAreNonBillable')}
                  <CheckBoxHelpText>
                    {i18n.t('Merchants:newInventoryChecksAreNonBillableHelp')}
                  </CheckBoxHelpText>
                </CheckboxLabel>
              </VytalCheckboxLabelContainer>
            </div>
          </div>
        </EditItem>
        <EditItem title={i18n.t('Merchants:nonUsageFee')}>
          {storeUpdate.charge_non_usage_fee && (
            <p>The partner will be charged for unused containers.</p>
          )}
          {!storeUpdate.charge_non_usage_fee && (
            <p>
              The partner will <b>NOT</b> be charged for unused containers.
            </p>
          )}
          {storeUpdate.is_net_receiver && <p>The partner is a net receiver.</p>}
          {!storeUpdate.is_net_receiver && (
            <p>
              The partner is <b>NOT</b> a net receiver.
            </p>
          )}
          <div className="input-group mb-3">
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={storeUpdate.non_usage_fee_enabled || false}
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.non_usage_fee_enabled,
                    'non_usage_fee_enabled',
                  )
                }
              />
              <CheckboxLabel
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.non_usage_fee_enabled,
                    'non_usage_fee_enabled',
                  )
                }
              >
                {i18n.t('Merchants:nonUsageFeeEnabled')}
                <CheckBoxHelpText>
                  {i18n.t('Merchants:nonUsageFeeEnabledHelp')}
                </CheckBoxHelpText>
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
            <div className="input-group mt-3 mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Merchants:nufComment')}
              </span>
              <input
                className="form-control"
                type="text"
                value={storeUpdate.nuf_comment || ''}
                onChange={(e) =>
                  handleChangeManually(e.target.value, 'nuf_comment')
                }
              />
            </div>
            <div className="input-group mt-3 mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('Merchants:excludedFromNufUntil')}
                </span>
              </div>
              <input
                className="form-control"
                type="date"
                value={(storeUpdate.excluded_from_nuf_until || '').substring(
                  0,
                  10,
                )}
                onChange={(e) =>
                  handleChangeManually(
                    e.target.value,
                    'excluded_from_nuf_until',
                  )
                }
              />
            </div>
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={storeUpdate.send_nuf_info_to_invoice_address || false}
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.send_nuf_info_to_invoice_address,
                    'send_nuf_info_to_invoice_address',
                  )
                }
              />
              <CheckboxLabel
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.send_nuf_info_to_invoice_address,
                    'send_nuf_info_to_invoice_address',
                  )
                }
              >
                {i18n.t('Merchants:sendNufInfoToInvoiceAddress')}
                <CheckBoxHelpText>
                  {i18n.t('Merchants:sendNufInfoToInvoiceAddressHelp')}
                </CheckBoxHelpText>
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
            <div className="input-group mt-3 mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Merchants:additionalAllowance')}
              </span>
              <input
                className={
                  'form-control ' +
                  (hasInvalidAdditionalAllowance() ? 'is-invalid' : '')
                }
                value={storeUpdate.additional_allowance || ''}
                onChange={(e) =>
                  handleChangeManually(
                    e.target.value.replace(/[^0-9.]/g, ''),
                    'additional_allowance',
                  )
                }
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Merchants:additionalAllowanceValidUntil')}
              </span>
              <input
                className={
                  'form-control ' +
                  (hasInvalidAdditionalAllowance() ? 'is-invalid' : '')
                }
                type="date"
                value={
                  storeUpdate.additional_allowance_valid_until || undefined
                }
                onChange={(e) =>
                  handleChangeManually(
                    e.target.value,
                    'additional_allowance_valid_until',
                  )
                }
              />
            </div>
          </div>
        </EditItem>
        <EditItem title={i18n.t('Merchants:storeOrderPreferences')}>
          <div className="input-group mb-3">
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={storeUpdate.order_preferences?.allow_koziol || false}
                onClick={toggleAllowKoziol}
              />
              <CheckboxLabel onClick={toggleAllowKoziol}>
                {i18n.t('Merchants:allowKoziol')}{' '}
                <CheckBoxHelpText>
                  {i18n.t('Merchants:allowKoziolHelp')}
                </CheckBoxHelpText>
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </div>
          <div className="input-group mb-3">
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={storeUpdate.order_preferences?.allow_mepal || false}
                onClick={toggleAllowMepal}
              />
              <CheckboxLabel onClick={toggleAllowMepal}>
                {i18n.t('Merchants:allowMepal')}{' '}
                <CheckBoxHelpText>
                  {i18n.t('Merchants:allowMepalHelp')}
                </CheckBoxHelpText>
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </div>
        </EditItem>
        <EditItem title={i18n.t('Merchants:storeToggleOptions')}>
          {storeUpdate.offline_timeout &&
            moment(storeUpdate.offline_timeout).isAfter(moment()) && (
              <div className="alert alert-warning mb-4" role="alert">
                {`The preorder and delivery function is currently disabled until: ${new Date(
                  storeUpdate.offline_timeout,
                ).toLocaleDateString(
                  i18n.language,
                  getDateFormatOptions(true),
                )}. Go to the `}
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={() => history.push(`${vytalStore.id}?activeTab=1`)}
                >
                  {i18n.t('Merchants:openingHoursTab')}
                </button>
                {` section ${i18n.t(
                  'OpeningHours:disableDeliveryTemporary',
                )} to enable preorder and delivery function`}
              </div>
            )}
          <div className="input-group mb-3">
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={storeUpdate.published || false}
                onClick={handlePublishedChanged}
              />
              <CheckboxLabel onClick={handlePublishedChanged}>
                {i18n.t('Merchants:published')}{' '}
                <CheckBoxHelpText>
                  {i18n.t('Merchants:publishedHelp')}
                </CheckBoxHelpText>
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </div>
          <div className="input-group mb-3">
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={storeUpdate.employee_only || false}
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.employee_only,
                    'employee_only',
                  )
                }
              />
              <CheckboxLabel onClick={handlePublishedChanged}>
                {i18n.t('Merchants:employeeOnly')}
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </div>
          <div className="input-group mb-3">
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={storeUpdate.can_preorder || false}
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.can_preorder,
                    'can_preorder',
                  )
                }
              />
              <CheckboxLabel
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.can_preorder,
                    'can_preorder',
                  )
                }
              >
                {i18n.t('Merchants:canPreorder')}
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </div>
          <div className="input-group mb-3">
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={storeUpdate.can_deliver || false}
                onClick={() =>
                  handleChangeManually(!storeUpdate.can_deliver, 'can_deliver')
                }
              />
              <CheckboxLabel
                onClick={() =>
                  handleChangeManually(!storeUpdate.can_deliver, 'can_deliver')
                }
              >
                {i18n.t('Merchants:canDeliver')}
                <CheckBoxHelpText>
                  {i18n.t('Merchants:canDeliverHelp')}
                </CheckBoxHelpText>
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </div>
          <div className="input-group mb-3">
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={storeUpdate.show_in_stats || false}
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.show_in_stats,
                    'show_in_stats',
                  )
                }
              />
              <CheckboxLabel
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.show_in_stats,
                    'show_in_stats',
                  )
                }
              >
                {i18n.t('Merchants:showInStatistics')}
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </div>
          <div className="input-group mb-3">
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={storeUpdate.coming_soon || false}
                onClick={() =>
                  handleChangeManually(!storeUpdate.coming_soon, 'coming_soon')
                }
              />
              <CheckboxLabel
                onClick={() =>
                  handleChangeManually(!storeUpdate.coming_soon, 'coming_soon')
                }
              >
                {i18n.t('Merchants:comingSoon')}
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </div>
          <div className="input-group mb-3">
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={storeUpdate.available_soon}
                onClick={() => {
                  handleChangeManually(
                    !storeUpdate.available_soon,
                    'available_soon',
                  );
                }}
              />
              <CheckboxLabel
                onClick={() => {
                  handleChangeManually(
                    !storeUpdate.available_soon,
                    'available_soon',
                  );
                }}
              >
                {i18n.t('Merchants:temporarilyUnavailable')}
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </div>
          {storeUpdate.available_soon && (
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Merchants:opens')}
              </span>
              <input
                type="date"
                className="form-control"
                data-isdate
                name="available_from"
                onChange={handleChange}
                value={
                  storeUpdate.available_from
                    ? DateTimeHelpers.toDateInputString(
                        storeUpdate.available_from,
                      )
                    : ''
                }
              />
              <button
                onClick={() => {
                  handleChangeManually('', 'available_from');
                }}
                className="btn btn-danger"
                type="button"
                id="button-addon2"
              >
                <FaTrash />
              </button>
            </div>
          )}
          <div className="input-group mb-3">
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={storeUpdate.permanently_closed || false}
                onClick={() => {
                  handleChangeManually(
                    !storeUpdate.permanently_closed,
                    'permanently_closed',
                  );
                }}
              />
              <CheckboxLabel
                onClick={() => {
                  handleChangeManually(
                    !storeUpdate.permanently_closed,
                    'permanently_closed',
                  );
                }}
              >
                {i18n.t('Merchants:permanentlyClosed')}
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </div>
          <div className="input-group mb-3">
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={storeUpdate.always_online || false}
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.always_online,
                    'always_online',
                  )
                }
              />
              <CheckboxLabel
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.always_online,
                    'always_online',
                  )
                }
              >
                {i18n.t('Merchants:keepAlwaysOnline')}
                <CheckBoxHelpText>
                  {i18n.t('Merchants:keepAlwaysOnlineHelp')}
                </CheckBoxHelpText>
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </div>
          <div className="input-group mb-3">
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={
                  storeUpdate.feature_container_collection_enabled || false
                }
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.feature_container_collection_enabled,
                    'feature_container_collection_enabled',
                  )
                }
              />
              <CheckboxLabel
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.feature_container_collection_enabled,
                    'feature_container_collection_enabled',
                  )
                }
              >
                {i18n.t('Merchants:allowUsingContainerCollections')}
                <CheckBoxHelpText>
                  {i18n.t('Merchants:allowUsingContainerCollectionsHelp')}
                </CheckBoxHelpText>
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </div>
          <div className="input-group mb-3">
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={storeUpdate.dpd_pickup_compulsory || false}
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.dpd_pickup_compulsory,
                    'dpd_pickup_compulsory',
                  )
                }
              />
              <CheckboxLabel
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.dpd_pickup_compulsory,
                    'dpd_pickup_compulsory',
                  )
                }
              >
                {i18n.t('Merchants:dpdPickupCompulsory')}
                <CheckBoxHelpText>
                  {i18n.t('Merchants:dpdPickupCompulsoryHelp')}
                </CheckBoxHelpText>
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </div>
          <div className="input-group mb-3">
            <VytalCheckboxLabelContainer>
              <Checkbox
                checked={storeUpdate.can_order_secondary_portfolio}
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.can_order_secondary_portfolio,
                    'can_order_secondary_portfolio',
                  )
                }
              />
              <CheckboxLabel
                onClick={() =>
                  handleChangeManually(
                    !storeUpdate.can_order_secondary_portfolio,
                    'can_order_secondary_portfolio',
                  )
                }
              >
                {i18n.t('Merchants:canOrderSecondaryPortfolio')}
                <CheckBoxHelpText>
                  {i18n.t('Merchants:canOrderSecondaryPortfolioHelp')}
                </CheckBoxHelpText>
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </div>
        </EditItem>
      </EditSection>
    </ContentSection>
  );
};
